import React from "react";
import { CheckCheck } from "lucide-react";

const OwnerShip = () => {
  return (
    <div className="min-h-screen bg-[var(--light-color)] px-4 py-10 text-[var(--dark-color)] font-sans">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 transition-all">
        {/* Title */}
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[var(--dark-blue)] mb-8 flex items-center gap-3">
          Code Ownership & Usage Rights
        </h1>

        {/* Section Template */}
        {[
          {
            title: "1. What You Pay For",
            content: (
              <>
                <p className="text-[var(--grey-color)] mb-4">
                  When we design or develop a website or software product for you:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    You pay for the development services and the right to use the final product.
                  </li>
                  <li>
                    We retain full ownership of the underlying code until those services are paid in full.
                  </li>
                </ul>
              </>
            ),
          },
          {
            title: "2. Usage License Only",
            content: (
              <>
                <p className="text-[var(--grey-color)]">
                  You receive a non-transferable, non‑exclusive license to use the product for your business purposes.
                </p>
                <p className="mt-2 text-[var(--grey-color)]">This license does not include:</p>
                <ul className="list-disc pl-5 mt-2 space-y-2">
                  <li>Rights to copy, distribute, resell, or sublicense the code.</li>
                  <li>Rights to modify or create derivatives beyond regular usage.</li>
                </ul>
              </>
            ),
          },
          {
            title: "3. When Ownership Transfers",
            content: (
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  On full payment of the agreed development fee (or separately negotiated assignment fee), we will:
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>Transfer the copyright or code ownership, or</li>
                    <li>Issue an exclusive license, as explicitly set out in your contract.</li>
                  </ul>
                </li>
              </ul>
            ),
          },
          {
            title: "4. Why We Retain Ownership Initially",
            content: (
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  To protect our pre‑existing, reusable components, templates, and frameworks used across projects.
                </li>
                <li>To prevent unlicensed reuse or resale of proprietary code.</li>
              </ul>
            ),
          },
          {
            title: "5. Typical Scenarios",
            content: (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 text-sm md:text-base">
                  <thead className="bg-[#eef4ff] font-semibold text-[var(--dark-color)]">
                    <tr>
                      <th className="border px-4 py-3 text-left">Scenario</th>
                      <th className="border px-4 py-3 text-left">What You Get</th>
                      <th className="border px-4 py-3 text-left">Can You Resell the Code?</th>
                    </tr>
                  </thead>
                  <tbody className="text-[var(--dark-color)]">
                    <tr className="hover:bg-gray-50">
                      <td className="border px-4 py-2">Standard Development Price</td>
                      <td className="border px-4 py-2">Usage license only</td>
                      <td className="border px-4 py-2">No</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="border px-4 py-2">Full payment + Ownership Agreement</td>
                      <td className="border px-4 py-2">All rights transferred</td>
                      <td className="border px-4 py-2">Yes (once transferred)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            title: "6. Legal Basis",
            content: (
              <>
                <p className="text-[var(--grey-color)]">
                  By default, the creator (us) owns the copyright in the code.
                </p>
                <p className="mt-2 text-[var(--grey-color)]">
                  Unless specified in writing (e.g., a work‑made‑for‑hire clause or ownership agreement), the developer retains ownership.
                </p>
                <p className="mt-2 text-[var(--grey-color)]">
                  Courts uphold licensing terms that clearly restrict transfer or resale.
                </p>
              </>
            ),
          },
        ].map(({ title, content }) => (
          <section key={title} className="mb-8">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              {title}
            </h2>
            <div className="text-[var(--dark-color)]">{content}</div>
          </section>
        ))}

        {/* Quote */}
        <div className="bg-[#eef4ff] border-l-4 border-[var(--dark-blue)] p-5 mt-10 italic text-sm text-[var(--dark-color)] rounded-md">
          “Until full payment is received, the developer retains all intellectual property rights and copyright
          in the code. The client is granted only a limited, non-transferable license to use the final website/product
          for internal business purposes. Any resale, distribution, modification, or sublicensing of the code is prohibited
          unless a separate ownership transfer agreement is executed upon final payment.”
        </div>

        {/* Final Notes */}
        <div className="bg-yellow-50 border-l-4 border-[var(--dark-yellow)] p-5 mt-6 text-sm rounded-md">
          <h3 className="font-semibold mb-2 text-[var(--dark-color)]">Why This Matters to You</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCheck size={18} className="text-[var(--dark-yellow)] mt-0.5" />
              Trust & transparency: You know exactly what you’re buying and what you get.
            </li>
            <li className="flex items-start gap-2">
              <CheckCheck size={18} className="text-[var(--dark-yellow)] mt-0.5" />
              Flexible control: You can opt for ownership transfer if needed.
            </li>
            <li className="flex items-start gap-2">
              <CheckCheck size={18} className="text-[var(--dark-yellow)] mt-0.5" />
              Risk avoidance: No confusion about resale or reuse rights.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OwnerShip;
